import React, { ReactNode, useContext } from 'react';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { EnterpriseContactsPeopleTable } from './data';
import { Contact } from 'models/Contact';
import { Button, Checkbox } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import classNames from 'classnames';
import ContactsContext from '../../../../../../providers/Contacts/Contacts.context';
import PencilIcon from 'icons/Pencil.icon';

type CellProps = {
  contact: Contact;
  isDeleting: boolean;
  handleEdit: (contact: Contact) => void;
};

const CheckboxCell: React.FC<CellProps> = ({ contact }) => {
  const { selected, updateSelected } = useContext(ContactsContext);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Checkbox
        checked={selected.some(({ _id }) => _id === contact._id)}
        onChange={() => {
          updateSelected(contact);
        }}
      />
    </div>
  );
};

const NameCell: React.FC<CellProps> = ({ contact }) => {
  const { firstName, lastName } = contact || {};

  return (
    <p>
      {firstName} {lastName}
    </p>
  );
};

const EmailCell: React.FC<CellProps> = ({ contact }) => {
  const { email } = contact || {};

  if (!email) return null;

  return <p>{email}</p>;
};

const MobileCell: React.FC<CellProps> = ({ contact }) => {
  const formattedPhone = formatPhoneNumber(contact?.phone);

  if (!formattedPhone) return null;

  return <p>{formattedPhone}</p>;
};

const CompanyCell: React.FC<CellProps> = ({ contact }) => {
  const { companyName } = contact || {};

  return <p>{companyName}</p>;
};

const ApprovedCell: React.FC<CellProps> = ({ contact }) => {
  const { preApproved } = contact || {};

  return (
    <div className={classNames('approved-cell', { approved: preApproved })}>
      {preApproved ? <CheckcircleIcon /> : <XcircleIcon />}
    </div>
  );
};

const EditCell: React.FC<CellProps> = ({ contact, handleEdit }) => {
  const { t } = useTranslation();

  return (
    <Button
      icon={PencilIcon}
      variant="link"
      onClick={(e) => {
        e.stopPropagation();
        handleEdit(contact);
      }}
    >
      {t('edit')}
    </Button>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof EnterpriseContactsPeopleTable,
  (cellProps: CellProps) => ReactNode
> = {
  checkbox: (cellProps: CellProps) => <CheckboxCell {...cellProps} />,
  firstName: (cellProps: CellProps) => <NameCell {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  phone: (cellProps: CellProps) => <MobileCell {...cellProps} />,
  companyName: (cellProps: CellProps) => <CompanyCell {...cellProps} />,
  preApproved: (cellProps: CellProps) => <ApprovedCell {...cellProps} />,
  edit: (cellProps: CellProps) => <EditCell {...cellProps} />,
};
